/* =====================================================================
 * Toast — macOS 스타일 알림 토스트 (frosted glass)
 * ===================================================================== */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import type { Toast as ToastType } from '@/store/uiStore';
import { useUIStore } from '@/store/uiStore';

/* ─── Animations ─── */
const slideIn = keyframes`
  0% {
    transform: translateX(calc(100% + 24px));
    opacity: 0;
  }
  60% {
    transform: translateX(-6px);
    opacity: 1;
  }
  80% {
    transform: translateX(2px);
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  100% {
    transform: translateX(calc(100% + 24px));
    opacity: 0;
  }
`;

const progressShrink = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`;

/* ─── Container ─── */
const Container = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 380px;
  width: 100%;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    right: 12px;
    left: 12px;
    max-width: none;
  }
`;

/* ─── Toast card ─── */
const ToastCard = styled.div<{ $type: ToastType['type']; $exiting?: boolean }>`
  pointer-events: all;
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  overflow: hidden;
  cursor: default;

  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(44, 44, 46, 0.78)'
      : 'rgba(255, 255, 255, 0.78)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);

  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 14px;

  box-shadow:
    0 4px 24px ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.45)'
        : 'rgba(0, 0, 0, 0.12)'},
    0 1px 3px ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.3)'
        : 'rgba(0, 0, 0, 0.06)'};

  animation: ${({ $exiting }) => ($exiting ? slideOut : slideIn)}
    ${({ $exiting }) => ($exiting ? '0.28s' : '0.45s')}
    cubic-bezier(0.34, 1.4, 0.64, 1) forwards;

  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow:
      0 6px 30px ${({ theme }) =>
        theme.mode === 'dark'
          ? 'rgba(0, 0, 0, 0.55)'
          : 'rgba(0, 0, 0, 0.16)'},
      0 1px 4px ${({ theme }) =>
        theme.mode === 'dark'
          ? 'rgba(0, 0, 0, 0.35)'
          : 'rgba(0, 0, 0, 0.08)'};
  }
`;

/* ─── Icon ─── */
const typeIconColors: Record<ToastType['type'], string> = {
  success: '#34c759',
  error: '#ff3b30',
  warning: '#ff9500',
  info: '#007aff',
};

const IconBox = styled.div<{ $type: ToastType['type'] }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $type }) => `${typeIconColors[$type]}18`};
  color: ${({ $type }) => typeIconColors[$type]};
`;

/* ─── Text ─── */
const Body = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 1px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 0.8125rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.01em;
`;

const Description = styled.div`
  font-size: 0.75rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
  word-break: break-word;
`;

/* ─── Close ─── */
const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.18s ease;
  position: absolute;
  top: 8px;
  right: 8px;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.08)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  ${ToastCard}:hover & {
    opacity: 1;
    transform: scale(1);
  }
`;

/* ─── Progress bar ─── */
const ProgressTrack = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  overflow: hidden;
  border-radius: 0 0 14px 14px;
`;

const ProgressBar = styled.div<{
  $type: ToastType['type'];
  $duration: number;
  $paused?: boolean;
}>`
  width: 100%;
  height: 100%;
  transform-origin: left center;
  background: ${({ $type }) => typeIconColors[$type]};
  opacity: 0.55;
  animation: ${progressShrink} ${({ $duration }) => $duration}ms linear forwards;
  ${({ $paused }) =>
    $paused &&
    css`
      animation-play-state: paused;
    `}
`;

/* ─── Icon map ─── */
const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

/* ─── Individual toast ─── */
const ToastItem: React.FC<{ toast: ToastType }> = ({ toast }) => {
  const removeToast = useUIStore((s) => s.removeToast);
  const [exiting, setExiting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const Icon = iconMap[toast.type];
  const duration = toast.duration ?? 4000;

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(() => removeToast(toast.id), 300);
  }, [removeToast, toast.id]);

  return (
    <ToastCard
      $type={toast.type}
      $exiting={exiting}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <IconBox $type={toast.type}>
        <Icon size={18} strokeWidth={2.2} />
      </IconBox>
      <Body>
        <Title>{toast.title}</Title>
        {toast.description && <Description>{toast.description}</Description>}
      </Body>
      <CloseBtn onClick={handleClose} aria-label="닫기">
        <X size={13} strokeWidth={2.5} />
      </CloseBtn>
      {duration > 0 && (
        <ProgressTrack>
          <ProgressBar
            $type={toast.type}
            $duration={duration}
            $paused={hovered}
          />
        </ProgressTrack>
      )}
    </ToastCard>
  );
};

/* ─── ToastContainer ─── */
export const ToastContainer: React.FC = () => {
  const toasts = useUIStore((s) => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <Container>
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </Container>
  );
};
